<template>
    <div class="flex gap-4 mt-12">
        <div class="w-1/2 mt-[18px] bg-white">
            <div
                class="w-full h-full border border-gray-300 rounded-[6px]"
            >

            <PaymentMethods
                class="p-6"
                :subscription-type="paymentRequest.type"
                :billing-address="billingAddress"
                @select-payment-method="handleSelectPaymentMethod"
                @loading="paymentMethodsLoading = $event"
            />

                <div class="flex flex-col lg:flex-row lg:justify-between">
                    <div
                        v-show="!paymentMethodsLoading"
                        class="w-full text-left"
                    >
                        <div class="border-t border-gray-300 p-6">
                            <CheckboxInput
                                v-model:checked="invoiceDetails"
                                size="lg"
                                name="invoiceDetails"
                                label="Add invoice details"
                            />
                        </div>

                        <InvoiceDetails
                            v-if="invoiceDetails"
                            :billing-address="billingAddress"
                            :countries="countries"
                            @updated-billing="emit('updatedBilling')"
                            @loading-invoice-details="
                                loadingInvoiceDetails = $event
                            "
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-[18px] w-1/2">
            <div
                class="w-full rounded-[6px] border border-blue-200 bg-blue-100 px-6 pb-5 pt-6"
            >
                <h4
                    class="text-lg font-medium leading-normal tracking-tighter text-gray-800"
                >
                    Summary
                </h4>
                <div
                    v-for="item in paymentRequest.items"
                    :key="item.id"
                    class="mt-4 flex items-start justify-between pb-[14px]"
                >
                    <div>
                        <p
                            class="text-sm font-medium leading-normal text-gray-800"
                        >
                            {{ item.name }}
                        </p>
                    </div>
                    <div
                        class="text-sm font-medium leading-normal text-gray-800"
                    >
                        ${{ item.formattedTotal }}
                    </div>
                </div>

                <div class="border-t border-blue-200 pt-5">
                    <div class="flex justify-between">
                        <div
                            class="text-lg font-medium leading-normal tracking-tighter text-gray-800"
                        >
                            Total
                        </div>
                        <div class="text-lg font-bold text-gray-800">
                            ${{ paymentRequest.formattedTotal }}
                        </div>
                    </div>
                    
                    <div class="mt-6">
                        <button 
                        :disabled="
                            loading ||
                            loadingInvoiceDetails ||
                            !selectedCard.token  ||
                            paymentMethodsLoading
                            "
                        
                        class="flex h-15 w-full items-center justify-center rounded-[6px] bg-[#4299E1] text-center text-lg font-bold text-white transition duration-200 ease-in-out hover:shadow-button focus:ring-2 focus:ring-[#4299E1] focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-white"
                        @click="handleSubmit" 
                        > Confirm Purchase </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { route } from 'ziggy-js';
import {create} from "@composables/useNotifications";
import {router} from "@inertiajs/vue3";
import {ICreditCardPaymentMethod, IPaymentMethod} from "@/types/Subscription/IPaymentMethod";
import {PropType, ref} from "vue";
import ISelectable from "@/types/Atomic/ISelectable";
import BillingAddress from "@/types/Subscription/BillingAddress";
import { useBraintreeClient } from '@/composables/useBraintree';

interface IselectedCard {
    token: string;
    type: 'Card' | 'PayPal' | '';
}

const props = defineProps({
    paymentRequest: {
        type: Object,
        required: true,
    },
    billingAddress: {
        type: Object as PropType<BillingAddress>,
        required: true,
    },
    countries: {
        required: true,
        type: Array as PropType<ISelectable[]>,
    },
});

const emit = defineEmits(['success', 'error', 'updatedBilling']);

const selectedCard = ref({ id: '', token: '', type: '' } as IselectedCard);

const paymentMethodsLoading = ref(true);
const loading = ref(false);
const invoiceDetails = ref(false);
const loadingInvoiceDetails = ref(false);

const { teamFeatureDisabled } = useFeatures();

const isStripeEnabled = !teamFeatureDisabled('stripe');

const {
    createClient,
    verifyCardByToken,
} = useBraintreeClient(props.paymentRequest.type);

const { fetchPaymentRequestData } = useBanners();

const handleSelectPaymentMethod = (paymentMethod: IPaymentMethod) => {
    selectedCard.value = {
        token: paymentMethod.token,
        type:
            (paymentMethod as ICreditCardPaymentMethod).bin !== undefined
                ? 'Card'
                : 'PayPal',
    };
};

const handleSubmit = async () => {
    try {
        loading.value = true;

        if(isStripeEnabled){
            await payWithStripe();
        } else {
            await payWithBraintree();
        }
        await fetchPaymentRequestData();
    } catch (err) {
        console.log(err);
    } finally {
        loading.value = false;
    }
};

const payWithBraintree = async () => {
    if (selectedCard.value === null) {
        return;
    }

    await createClient({ enableHostedFields: false });

    let verifyCardResponse = undefined;

    if (selectedCard.value.type === 'Card') {
        verifyCardResponse = await verifyCardByToken({
            token: selectedCard.value.token,
            amount: props.paymentRequest.total * 100,
        });
    }

    return new Promise((resolve, reject) => {
        router.post(
            route('payment-requests.pay', {
                paymentRequest: props.paymentRequest.uuid,
            }),
            {
                nonce: verifyCardResponse ? verifyCardResponse.nonce : '',
                token: selectedCard.value.token,
                type: selectedCard.value.type,
            },
            {
                preserveScroll: true,
                onSuccess: (response) => {
                    create({
                        title: 'Payment success',
                        text: 'Payment request has been paid.',
                        type: 'success',
                    });
                    resolve(response);
                },
                onError: (response) => {
                    create({
                        title: 'Payment failed',
                        text:
                            'Following error occurred "' +
                            response.message +
                            '" when charging you card.',
                        type: 'error',
                    });
                    reject(response);
                },
            }
        );
    });
};

const payWithStripe = async () => {
    return new Promise((resolve, reject) => {
        router.post(
            route('payment-requests.stripe.pay', {
                paymentRequest: props.paymentRequest.uuid,
            }),
            {
                payment_method_id: selectedCard.value.token,
                type: props.paymentRequest.type
            },
            {
                preserveScroll: true,
                onSuccess: (response) => {
                    create({
                        title: 'Payment success',
                        text: 'Payment request has been paid.',
                        type: 'success',
                    });
                    resolve(response);
                },
                onError: (response) => {
                    create({
                        title: 'Payment failed',
                        text:
                            'Following error occurred "' +
                            response.message +
                            '" when charging you card.',
                        type: 'error',
                    });
                    reject(response);
                },
            }
        );
    });
}
</script>
